import BigNumber from 'bignumber.js';
import { bunja } from 'bunja';
import { atom } from 'jotai';
import { RESET, atomWithDefault } from 'jotai/utils';

import type { TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { overlayItemBunja } from '@flipster/overlay';
import { atomWithBigNumber } from '@flipster/react/misc/state/atom-with-bignumber';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import Num from '@flipster/utils/num';

import {
  onBasisModeChange,
  syncOnLimitPriceChange,
  syncOnSecondaryChange,
  syncOnTriggerPriceChange,
  targetPriceToBasisValue,
  type ModalBasisMode,
  type PriceBasisState,
  type TpslBasisContext,
} from '../../shared/calculator/tpsl-basis';
import { formatTpslConvertedPrice } from '../../shared/tpsl/format-converted-price';
import { formatModalSecondaryDisplay } from '../../shared/tpsl/tpsl-basis-field/placeholders';
import {
  defaultModalLegBasisModes,
  persistModalEntranceBasisModes,
  readModalEntranceBasisModes,
  resolveTpslModalBasisEntrance,
  type ModalLegBasisModes,
} from '../../shared/tpsl/tpsl-basis-persistence';
import { InitialTriggerPriceScope, selectedTpslOrderBunja, tpslModalBunja } from '../state';
import { instrumentBunja, parentPositionBunja, resolveTriggerReferencePrice, tickerPriceBunja } from '../state/base';
import { resolveInitialTpslTriggerPriceType } from '../state/tpsl-edit-prefill';
import { tpslModalApiBunja } from './api';
import { isTriggerCloseToLiquidation } from './liquidation-warning';
import { tpslFormSegmentBunja } from './segment';
import {
  computeModalIsDirty,
  exceedsPositionQty,
  nextChangeSecondaryAfterQuickDelta,
  pickInitialBaseTriggerPrice,
  resolvePartialQtyPrefill,
  roundDownWithQuantum,
  roundHalfDownWithQuantum,
} from './tpsl-utils';
import { tpslModalValidationBunja } from './validation';

export const tpslFormContentBunja = bunja(() => {
  const { close } = bunja.use(overlayItemBunja);
  const { symbol, tpslTabAtom, canPartial, canLimitTpslAtom, tpslType, isTimeTriggerOrder, positionType } =
    bunja.use(tpslModalBunja);
  const { confirmTpsl } = bunja.use(tpslModalApiBunja);
  const { submitDisabledAtom } = bunja.use(tpslModalValidationBunja);
  const { trackTpslSettingView, trackTpslSettingConfirmClick } = bunja.use(tpslFormSegmentBunja);
  const { selectedTpslOrder } = bunja.use(selectedTpslOrderBunja);
  const { isLimitTpslOrderAtom, resetForm } = bunja.use(tpslModalInputBunja);
  const { isCloseToLiquidationAtom } = bunja.use(tpslModalInputDerivationBunja);

  const mode = selectedTpslOrder ? 'update' : 'add';

  const secondaryButtonTypeAtom = atom<'remove' | undefined>((get) => {
    if (positionType !== 'open') return undefined;
    const tpslTab = get(tpslTabAtom);
    if (tpslTab === 'entire' && mode === 'update') return 'remove';
    return undefined;
  });

  function onConfirmButtonClick() {
    trackTpslSettingConfirmClick();
    return confirmTpsl();
  }

  bunja.effect(trackTpslSettingView);

  bunja.effect(() => {
    if (mode === 'add') resetForm();
  });

  return {
    symbol,
    mode,
    onClose: close,
    onConfirmButtonClick,
    submitDisabledAtom,
    canPartial,
    tpslTabAtom,
    tpslType,
    isTimeTriggerOrder,
    secondaryButtonTypeAtom,
    selectedOrder: selectedTpslOrder,
    isLimitTpslOrderAtom,
    canLimitTpslAtom,
    isCloseToLiquidationAtom,
  };
});
tpslFormContentBunja.debugLabel = 'tpslFormContentBunja';

export const tpslModalInputBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { lastPriceAtom, markPriceAtom: tickerMarkPriceAtom } = bunja.use(tickerPriceBunja);
  const { tickSizeAtom, pricePrecisionAtom } = bunja.use(instrumentBunja);
  const {
    entryOrOrderPriceAtom,
    liqPriceAtom,
    marginType,
    sideAtom,
    markPriceAtom,
    pendingPositionAtom,
    openPositionAtom,
    isolatedMarginInitialAtom,
    isolatedMarginAddedAtom,
    orderPriceAtom,
    leverageAtom,
  } = bunja.use(parentPositionBunja);
  const { positionQtyAtom, tpslTabAtom, positionType } = bunja.use(tpslModalBunja);
  const { unitOrderQtyAtom, tradeQtyPrecisionAtom } = bunja.use(instrumentBunja);
  // Flow A: 차트 드래그로 모달을 열 때 trigger price 입력을 prefill할 초기값(생성 모드에서만 사용).
  const initialTriggerPriceFromDrag = bunja.use(InitialTriggerPriceScope);
  const {
    selectedTpslOrder,
    tpslTriggerPriceAtom: selectedTpslTriggerPriceAtom,
    tpslTriggerPriceTypeAtom: selectedTpslTriggerPriceTypeAtom,
    tpslQtyPercentAtom,
    tpslOrderQtyAtom: selectedTpslOrderQtyAtom,
    tpslOrderTypeAtom,
    tpslLimitPriceAtom,
    tpslIsBboAtom,
  } = bunja.use(selectedTpslOrderBunja);

  // Toggling the Limit Order checkbox or the BBO checkbox changes the execution
  // type of the order — that counts as a modification, so Confirm must activate
  // even when the user hasn't touched price or qty.
  const executionDirtyAtom = atom(false);

  // Whether the user has chosen to place the TP/SL as a Limit order instead of a Market order.
  // Distinct from `tpslModalBunja.isLimitOrder`, which reflects the parent pending order's type.
  // In update mode we pre-check the box when the existing TP/SL is already Limit (Limit or BBO).
  const _isLimitTpslOrderAtom = atomWithDefault((get) => get(tpslOrderTypeAtom) === 'Limit');
  const isLimitTpslOrderAtom = atom(
    (get) => get(_isLimitTpslOrderAtom),
    (_get, set, value: boolean) => {
      set(_isLimitTpslOrderAtom, value);
      set(executionDirtyAtom, true);
    },
  );
  // When true, the limit price follows the best counterparty (bid/offer) at trigger time.
  // Defaults to true when the existing standalone TP/SL is a Limit row with no user-set
  // limit price (the server stamps BBO's resolved price at trigger time).
  const _isBboAtom = atomWithDefault((get) => get(tpslIsBboAtom));
  const isBboAtom = atom(
    (get) => get(_isBboAtom),
    (_get, set, value: boolean) => {
      set(_isBboAtom, value);
      set(executionDirtyAtom, true);
    },
  );

  // Variant B basis modes (view-only) — absolute prices remain canonical.
  const triggerBasisModeAtom = atom<ModalBasisMode>(defaultModalLegBasisModes.trigger);
  const limitBasisModeAtom = atom<ModalBasisMode>(defaultModalLegBasisModes.limit);
  const triggerSecondaryAtom = atom('');
  const limitSecondaryAtom = atom('');

  const isLimitFixedAtom = atom((get) => get(isLimitTpslOrderAtom) && !get(isBboAtom));

  const _triggerPriceTypeAtom = atomWithDefault((get) =>
    resolveInitialTpslTriggerPriceType(get(selectedTpslTriggerPriceTypeAtom), get(pendingPositionAtom)),
  );
  const triggerPriceTypeAtom = atom(
    (get) => get(_triggerPriceTypeAtom),
    (_get, set, value: TriggerPriceType) => {
      set(_triggerPriceTypeAtom, value);
      set(priceDirtyAtom, true);
    },
  );
  const triggerReferencePriceAtom = atomWithBigNumber((get) =>
    resolveTriggerReferencePrice(get(triggerPriceTypeAtom), {
      lastPrice: get(lastPriceAtom),
      markPrice: get(tickerMarkPriceAtom),
    }),
  );

  const priceDirtyAtom = atom(false);
  const qtyDirtyAtom = atom(false);
  // 차트 드래그/드롭으로 trigger price가 prefill되면 사용자가 가격을 지정한 것이므로, 입력을 직접
  // 건드리지 않아도 제출 가능(dirty)으로 본다. (빈/오류 입력은 submitDisabledAtom의
  // hasTriggerPriceInputError 검사가 우선 차단하므로 안전)
  const hasDraggedTriggerPrice = initialTriggerPriceFromDrag != null && initialTriggerPriceFromDrag > 0;
  const modalIsDirtyAtom = atom((get) =>
    computeModalIsDirty({
      hasDraggedTriggerPrice,
      priceDirty: get(priceDirtyAtom),
      qtyDirty: get(qtyDirtyAtom),
      executionDirty: get(executionDirtyAtom),
    }),
  );

  const initialBaseTriggerPriceAtom = atom((get) => {
    const isUpdateMode = Boolean(selectedTpslOrder);
    const entryOrOrderPrice = get(entryOrOrderPriceAtom);
    const selectedTpslTriggerPrice = Num(get(selectedTpslTriggerPriceAtom) || 0);

    return pickInitialBaseTriggerPrice({
      isUpdateMode,
      selectedTriggerPrice: selectedTpslTriggerPrice,
      entryOrOrderPrice,
      draggedTriggerPrice: initialTriggerPriceFromDrag,
    });
  });

  const tpslTriggerPriceAtom = atomWithDefault((get) => get(initialBaseTriggerPriceAtom));

  const _triggerPriceInputTextAtom = atomWithDefault((get) => {
    const price = get(initialBaseTriggerPriceAtom);
    return price.isZero() ? '' : String(price);
  });
  const triggerPriceInputTextAtom = atom(
    (get) => get(_triggerPriceInputTextAtom),
    (_get, set, value: string) => {
      set(_triggerPriceInputTextAtom, value);
      set(priceDirtyAtom, true);
    },
  );
  const triggerPriceInputIsEmptyAtom = atom((get) => get(triggerPriceInputTextAtom) === '');
  const triggerPriceInputValueAtom = atom(
    (get) => Num(get(triggerPriceInputTextAtom) || '0'),
    (get, set, value: BigNumber) => {
      const tickSize = get(tickSizeAtom);
      const nonNegativeValue = value.lte(0) ? Num(0) : value;
      set(triggerPriceInputTextAtom, String(roundHalfDownWithQuantum(nonNegativeValue, tickSize)));
    },
  );

  // --- Limit Price (only used when isLimitTpslOrderAtom is true) ---
  // Update mode: seed from the existing order's Limit price when it has one; fall
  // back to the trigger-price default. (BBO rows have no limit price server-side,
  // so we also fall back for those.)
  const limitBaseTriggerPriceAtom = atom((get) => {
    const existingLimitPrice = get(tpslLimitPriceAtom);
    if (existingLimitPrice) return Num(existingLimitPrice);
    return get(initialBaseTriggerPriceAtom);
  });
  const limitPercentBasePriceAtom = atomWithDefault((get) => get(limitBaseTriggerPriceAtom));

  const _limitPriceInputTextAtom = atomWithDefault((get) => {
    const price = get(limitBaseTriggerPriceAtom);
    return price.isZero() ? '' : String(price);
  });
  const limitPriceInputTextAtom = atom(
    (get) => get(_limitPriceInputTextAtom),
    (_get, set, value: string) => {
      set(_limitPriceInputTextAtom, value);
      set(priceDirtyAtom, true);
    },
  );
  const limitPriceInputIsEmptyAtom = atom((get) => get(limitPriceInputTextAtom) === '');
  const limitPriceInputValueAtom = atom(
    (get) => Num(get(limitPriceInputTextAtom) || '0'),
    (get, set, value: BigNumber) => {
      const tickSize = get(tickSizeAtom);
      const nonNegativeValue = value.lte(0) ? Num(0) : value;
      set(limitPriceInputTextAtom, String(roundHalfDownWithQuantum(nonNegativeValue, tickSize)));
    },
  );
  const limitPriceDiffFromEntryPriceAtom = atom((get) => {
    const text = get(limitPriceInputTextAtom);
    const value = get(limitPriceInputValueAtom);
    const entryOrOrderPrice = get(entryOrOrderPriceAtom);
    if (text === '' || entryOrOrderPrice.isZero()) return Num(0);
    return value.minus(entryOrOrderPrice).dividedBy(entryOrOrderPrice).multipliedBy(100);
  });

  function formatAbsolutePrice(price: string): string {
    const pricePrecision = store.get(pricePrecisionAtom);
    // Converted secondary → price: ROUND_DOWN. Direct price input atoms stay HALF_DOWN.
    return formatTpslConvertedPrice(price, pricePrecision);
  }

  function formatSecondaryDisplay(value: string | null | undefined): string {
    return formatModalSecondaryDisplay(value);
  }

  function resolveMargin(): string {
    if (positionType === 'open') {
      const position = store.get(openPositionAtom);
      if (!position) return '0';
      switch (marginType) {
        case 'Cross':
          return String(position.initPositionMargin);
        case 'Isolated':
          return Num(store.get(isolatedMarginInitialAtom)).plus(store.get(isolatedMarginAddedAtom)).toString();
        default:
          return '0';
      }
    }
    const positionQty = store.get(positionQtyAtom);
    const orderPrice = store.get(orderPriceAtom);
    const leverage = store.get(leverageAtom);
    return positionQty.multipliedBy(orderPrice).dividedBy(leverage).toString();
  }

  function getBasisCtx(): TpslBasisContext {
    const side = store.get(sideAtom) as 'Long' | 'Short';
    const qtyAbs = store.get(tpslOrderQtyAtom);
    const signedQty =
      side === 'Short'
        ? Num(qtyAbs || '0')
            .negated()
            .toString()
        : qtyAbs;
    return {
      side,
      entryRefPrice: String(store.get(entryOrOrderPriceAtom)),
      qty: signedQty,
      margin: resolveMargin(),
      formulaProfile: 'position',
    };
  }

  function readTriggerPriceBasisState(): PriceBasisState {
    return {
      triggerPrice: store.get(_triggerPriceInputTextAtom),
      limitPrice: store.get(_limitPriceInputTextAtom),
      secondaryValue: store.get(triggerSecondaryAtom),
      basisMode: store.get(triggerBasisModeAtom),
      isLimitFixed: store.get(isLimitFixedAtom),
    };
  }

  function readLimitPriceBasisState(): PriceBasisState {
    return {
      triggerPrice: store.get(_triggerPriceInputTextAtom),
      limitPrice: store.get(_limitPriceInputTextAtom),
      secondaryValue: store.get(limitSecondaryAtom),
      basisMode: 'change',
      isLimitFixed: true,
    };
  }

  function recomputeSecondaryFromPrice(which: 'trigger' | 'limit'): void {
    const ctx = getBasisCtx();
    if (which === 'trigger') {
      const mode = store.get(triggerBasisModeAtom);
      const price = store.get(_triggerPriceInputTextAtom);
      store.set(triggerSecondaryAtom, formatSecondaryDisplay(targetPriceToBasisValue(mode, price, ctx)));
      return;
    }
    const price = store.get(_limitPriceInputTextAtom);
    store.set(limitSecondaryAtom, formatSecondaryDisplay(targetPriceToBasisValue('change', price, ctx)));
  }

  function recomputeAllSecondariesFromPrices(): void {
    recomputeSecondaryFromPrice('trigger');
    if (store.get(isLimitTpslOrderAtom) && !store.get(isBboAtom)) {
      recomputeSecondaryFromPrice('limit');
    }
  }

  function hydrateBasisModesFromPersistence(): void {
    const entrance = resolveTpslModalBasisEntrance(positionType, store.get(tpslTabAtom));
    const modes: ModalLegBasisModes = readModalEntranceBasisModes(entrance) ?? defaultModalLegBasisModes;
    store.set(triggerBasisModeAtom, modes.trigger);
    // Limit secondary is Change(%) only across all modals.
    store.set(limitBasisModeAtom, 'change');
    recomputeAllSecondariesFromPrices();
  }

  function persistBasisModesOnConfirm(): void {
    const entrance = resolveTpslModalBasisEntrance(positionType, store.get(tpslTabAtom));
    persistModalEntranceBasisModes(entrance, {
      trigger: store.get(triggerBasisModeAtom),
      limit: 'change',
    });
  }

  function getTriggerPriceDefault(): string {
    const entry = store.get(entryOrOrderPriceAtom);
    return entry.isZero() ? '' : String(entry);
  }

  function changeTriggerBasisMode(mode: ModalBasisMode): void {
    const next = onBasisModeChange(readTriggerPriceBasisState(), mode, {
      triggerPriceDefault: getTriggerPriceDefault(),
    });
    store.set(triggerBasisModeAtom, next.basisMode);
    store.set(triggerSecondaryAtom, next.secondaryValue);
    if (next.triggerPrice !== store.get(_triggerPriceInputTextAtom)) {
      store.set(triggerPriceInputTextAtom, next.triggerPrice);
    }
  }

  function changeLimitBasisMode(_mode: ModalBasisMode): void {
    // Limit secondary is Change(%) only — ignore mode switches.
    const next = onBasisModeChange(readLimitPriceBasisState(), 'change', {
      triggerPriceDefault: getTriggerPriceDefault(),
    });
    store.set(limitBasisModeAtom, 'change');
    store.set(limitSecondaryAtom, next.secondaryValue);
  }

  function onTriggerSecondaryChange(nextSecondary: string): void {
    const ctx = getBasisCtx();
    // Trigger row only — never equalize Limit when Limit Order is on.
    const state = { ...readTriggerPriceBasisState(), isLimitFixed: false };
    const next = syncOnSecondaryChange(state, nextSecondary, ctx);
    store.set(triggerSecondaryAtom, nextSecondary);
    if (next.triggerPrice !== state.triggerPrice) {
      const tickSize = store.get(tickSizeAtom);
      const rounded = roundDownWithQuantum(Num(next.triggerPrice || '0'), tickSize);
      const text = rounded.isZero() && next.triggerPrice === '' ? '' : formatAbsolutePrice(String(rounded));
      store.set(triggerPriceInputTextAtom, text);
    }
  }

  function onLimitSecondaryChange(nextSecondary: string): void {
    const ctx = getBasisCtx();
    // Limit row only — do not move Trigger price/secondary.
    const state = readLimitPriceBasisState();
    const next = syncOnSecondaryChange(state, nextSecondary, ctx);
    store.set(limitSecondaryAtom, nextSecondary);
    if (next.limitPrice !== state.limitPrice) {
      const tickSize = store.get(tickSizeAtom);
      const rounded = roundDownWithQuantum(Num(next.limitPrice || '0'), tickSize);
      store.set(limitPriceInputTextAtom, formatAbsolutePrice(String(rounded)));
    }
  }

  function updateLimitPercentBasePrice(): void {
    const value = store.get(limitPriceInputValueAtom);
    store.set(
      limitPercentBasePriceAtom,
      value.isZero() ? store.get(limitBaseTriggerPriceAtom) : clampTriggerPrice(value),
    );
  }

  function setLimitPriceInputText(value: string, doUpdatePercentBasePrice: boolean = false): void {
    const ctx = getBasisCtx();
    const state = readLimitPriceBasisState();
    const next = syncOnLimitPriceChange(state, value, ctx);
    store.set(limitPriceInputTextAtom, next.limitPrice);
    store.set(limitSecondaryAtom, formatSecondaryDisplay(next.secondaryValue));
    if (doUpdatePercentBasePrice) updateLimitPercentBasePrice();
  }

  function setLimitPriceInputValueFromEntry(): void {
    const text = String(store.get(entryOrOrderPriceAtom));
    setLimitPriceInputText(text, true);
  }

  function updateLimitPriceByPercent(percent: number): void {
    // Limit secondary is Change(%) only — ±% adjusts Change, then absolute is derived (+ tick).
    const nextSecondary = nextChangeSecondaryAfterQuickDelta(store.get(limitSecondaryAtom), percent);
    onLimitSecondaryChange(nextSecondary);
  }

  function updatePercentBasePrice(): void {
    const value = store.get(triggerPriceInputValueAtom);
    store.set(tpslTriggerPriceAtom, value.isZero() ? store.get(initialBaseTriggerPriceAtom) : clampTriggerPrice(value));
  }

  function setTriggerPriceInputText(value: string, doUpdatePercentBasePrice: boolean = false): void {
    const ctx = getBasisCtx();
    const state = readTriggerPriceBasisState();
    // Trigger-row secondary always tracks trigger price (dual-field).
    const next = syncOnTriggerPriceChange({ ...state, isLimitFixed: false }, value, ctx);
    store.set(triggerPriceInputTextAtom, next.triggerPrice);
    store.set(triggerSecondaryAtom, formatSecondaryDisplay(next.secondaryValue));
    if (doUpdatePercentBasePrice) updatePercentBasePrice();
  }

  function setInputValueFromMarket(): void {
    const text = String(store.get(triggerReferencePriceAtom));
    setTriggerPriceInputText(text, true);
  }

  function setInputValueFromEntry(): void {
    const text = String(store.get(entryOrOrderPriceAtom));
    setTriggerPriceInputText(text, true);
  }

  function updateByPercent(percent: number): void {
    // Change-mode ±% adjusts Change(%) secondary (button label), then absolute is derived (+ tick).
    // Keep secondary as the summed Change (same as typing) so UI shows 0.10 not a tick-reforwards 0.09.
    if (store.get(triggerBasisModeAtom) !== 'change') return;
    const nextSecondary = nextChangeSecondaryAfterQuickDelta(store.get(triggerSecondaryAtom), percent);
    onTriggerSecondaryChange(nextSecondary);
    const clamped = clampTriggerPrice(store.get(triggerPriceInputValueAtom));
    if (!clamped.eq(store.get(triggerPriceInputValueAtom))) {
      setTriggerPriceInputText(formatAbsolutePrice(String(clamped)));
    }
  }

  function resetPrices(): void {
    store.set(_triggerPriceInputTextAtom, RESET);
    store.set(tpslTriggerPriceAtom, RESET);
    store.set(_limitPriceInputTextAtom, RESET);
    store.set(limitPercentBasePriceAtom, RESET);
    store.set(priceDirtyAtom, false);
  }

  function resetForm(): void {
    resetPrices();
    store.set(partialQtyAtom, RESET);
    store.set(partialQtyPercentAtom, RESET);
    store.set(_isLimitTpslOrderAtom, RESET);
    store.set(_isBboAtom, RESET);
    store.set(_triggerPriceTypeAtom, RESET);
    store.set(qtyDirtyAtom, false);
    store.set(executionDirtyAtom, false);
    hydrateBasisModesFromPersistence();
  }

  // 청산가를 넘겨서 tp/sl을 설정하는건 의미가 없으므로 청산가를 넘기지 않도록 trigger price를 제한
  function clampTriggerPrice(targetTriggerPrice: BigNumber): BigNumber {
    const side = store.get(sideAtom);
    const liqPrice = store.get(liqPriceAtom);

    switch (marginType) {
      case 'Cross':
        return targetTriggerPrice;
      case 'Isolated':
        return side === 'Long'
          ? BigNumber.max(targetTriggerPrice, liqPrice)
          : BigNumber.min(targetTriggerPrice, liqPrice);
      default:
        return Num(0);
    }
  }

  // 신규(add) partial 기본 비율은 0%(드래그로 열어도 0). 수정 모드는 기존 주문 비율을 따른다.
  //
  // 기존 주문 비율은 100% 를 넘을 수 있다(포지션만 partial close 로 줄고 partial TP/SL qty 는 유지되는 경우).
  // 슬라이더 상한이 100% 라 그 값은 폼에서 표현할 수 없으므로 100% 로 낮춰 prefill 하고,
  // "TP amount exceeds your current position" warning(tpslAmountWarningMessageAtom)으로 상태를 알린다. (QA-21301)
  const partialQtyPercentAtom = atomWithDefault((get) =>
    selectedTpslOrder ? Math.min(100, get(tpslQtyPercentAtom)) : 0,
  );

  // 수정 모드의 qty 는 선택 주문의 qty 를 그대로 쓴다(비율에서 역산하지 않는다 — QA-21323).
  // 신규(add) 모드는 선택 주문이 없으므로 비율(0%) 기반으로 폴백한다. 두 값은
  // changeQtyPercent/changeQty/resetForm 에서 항상 함께 쓰이므로, 이 default 가 평가되는 시점이면
  // partialQtyPercentAtom 도 unset(=prefill 비율) 이다.
  const partialQtyAtom = atomWithDefault((get) =>
    resolvePartialQtyPrefill({
      selectedOrderQty: get(selectedTpslOrderQtyAtom),
      positionQty: get(positionQtyAtom),
      qtyPercent: get(partialQtyPercentAtom),
      unitOrderQty: get(unitOrderQtyAtom),
      tradeQtyPrecision: get(tradeQtyPrecisionAtom),
    }),
  );

  const partialQtyUsdtAtom = atomWithBigNumber((get) => {
    const qty = Num(get(partialQtyAtom) || '0');
    const markPrice = get(markPriceAtom);
    return qty.multipliedBy(markPrice);
  });

  function changeQtyPercent(percent: number) {
    const positionQty = store.get(positionQtyAtom);
    const unitOrderQty = store.get(unitOrderQtyAtom);
    const tradeQtyPrecision = store.get(tradeQtyPrecisionAtom);
    const qty = roundHalfDownWithQuantum(positionQty.multipliedBy(percent).dividedBy(100), unitOrderQty);
    store.set(partialQtyAtom, qty.toFixed(tradeQtyPrecision));
    store.set(partialQtyPercentAtom, percent);
    store.set(qtyDirtyAtom, true);
    // qty change → keep prices, recompute secondary
    recomputeAllSecondariesFromPrices();
  }

  function changeQty(qtyStr: string) {
    const positionQty = store.get(positionQtyAtom);
    // Over position size → clamp to 100% (same quantum path as the slider max).
    if (exceedsPositionQty(qtyStr, positionQty)) {
      changeQtyPercent(100);
      return;
    }
    store.set(partialQtyAtom, qtyStr);
    if (positionQty.isZero()) {
      store.set(qtyDirtyAtom, true);
      recomputeAllSecondariesFromPrices();
      return;
    }
    const qty = Num(qtyStr || '0');
    const percent = qty.dividedBy(positionQty).multipliedBy(100).integerValue(BigNumber.ROUND_HALF_UP).toNumber();
    store.set(partialQtyPercentAtom, Math.max(0, Math.min(100, percent)));
    store.set(qtyDirtyAtom, true);
    recomputeAllSecondariesFromPrices();
  }

  const tpslOrderQtyAtom = atom((get) => {
    const tpslTab = get(tpslTabAtom);

    switch (positionType) {
      case 'open': {
        return tpslTab === 'entire' ? get(positionQtyAtom).toString() : get(partialQtyAtom);
      }
      case 'pending': {
        // pending은 UI상으로 entire로 보이지만, 항상 100% partial qty 전달 (entire 개념 없음)
        // https://www.notion.so/prextech/TPSL-Revamp-Phase-3-328eb32ad2278097b955c3f34b8f6b5d?source=copy_link#57d474fa466b40a7b682a02df4b69aaa
        const fullQty = get(positionQtyAtom).toString();
        return fullQty;
      }
      default:
        return get(partialQtyAtom);
    }
  });

  // Restore basis modes (entrance × side) on mount and when Entire/Partial tab changes.
  bunja.effect(() => {
    hydrateBasisModesFromPersistence();
    const unsubTab = store.sub(tpslTabAtom, () => {
      hydrateBasisModesFromPersistence();
    });
    // Limit Order / BBO toggles change which price is the basis target — refresh secondaries.
    const unsubLimit = store.sub(isLimitTpslOrderAtom, () => {
      recomputeAllSecondariesFromPrices();
    });
    const unsubBbo = store.sub(isBboAtom, () => {
      recomputeAllSecondariesFromPrices();
    });
    return () => {
      unsubTab();
      unsubLimit();
      unsubBbo();
    };
  });

  return {
    modalIsDirtyAtom,
    qtyDirtyAtom,
    triggerPriceInputTextAtom,
    triggerPriceInputIsEmptyAtom,
    triggerPriceInputValueAtom,
    triggerPriceTypeAtom,
    triggerReferencePriceAtom,
    setTriggerPriceInputText,
    setInputValueFromMarket,
    setInputValueFromEntry,
    addPercent: updateByPercent,
    partialQtyAtom,
    partialQtyPercentAtom,
    partialQtyUsdtAtom,
    tpslOrderQtyAtom,
    changeQtyPercent,
    changeQty,
    isLimitTpslOrderAtom,
    isBboAtom,
    isLimitFixedAtom,
    limitPriceInputTextAtom,
    limitPriceInputIsEmptyAtom,
    limitPriceInputValueAtom,
    limitPriceDiffFromEntryPriceAtom,
    setLimitPriceInputText,
    setLimitPriceInputValueFromEntry,
    addLimitPercent: updateLimitPriceByPercent,
    triggerBasisModeAtom,
    limitBasisModeAtom,
    triggerSecondaryAtom,
    limitSecondaryAtom,
    changeTriggerBasisMode,
    changeLimitBasisMode,
    onTriggerSecondaryChange,
    onLimitSecondaryChange,
    persistBasisModesOnConfirm,
    hydrateBasisModesFromPersistence,
    resetPrices,
    resetForm,
  };
});
tpslModalInputBunja.debugLabel = 'tpslModalInputBunja';

export const tpslModalInputDerivationBunja = bunja(() => {
  const { positionQtyAtom } = bunja.use(tpslModalBunja);
  const {
    triggerPriceInputTextAtom,
    triggerPriceInputIsEmptyAtom,
    triggerPriceInputValueAtom,
    limitPriceInputIsEmptyAtom,
    limitPriceInputValueAtom,
    isLimitTpslOrderAtom,
    isBboAtom,
    tpslOrderQtyAtom,
  } = bunja.use(tpslModalInputBunja);
  const {
    positionType,
    marginType,
    sideAtom,
    leverageAtom,
    isolatedMarginInitialAtom,
    isolatedMarginAddedAtom,
    openPositionAtom,
    orderPriceAtom,
    liqPriceAtom,
    entryOrOrderPriceAtom,
  } = bunja.use(parentPositionBunja);

  const priceDiffFromEntryPriceAtom = atom((get) => {
    const triggerPriceInputText = get(triggerPriceInputTextAtom);
    const triggerPriceInputValue = get(triggerPriceInputValueAtom);
    const entryOrOrderPrice = get(entryOrOrderPriceAtom);
    if (triggerPriceInputText === '') return Num(0);
    return triggerPriceInputValue.minus(entryOrOrderPrice).dividedBy(entryOrOrderPrice).multipliedBy(100);
  });

  const marginForOpenPositionAtom = atom((get) => {
    const position = get(openPositionAtom);
    const isolatedMarginInitial = get(isolatedMarginInitialAtom);
    const isolatedMarginAdded = get(isolatedMarginAddedAtom);
    if (!position) return '0';
    switch (marginType) {
      case 'Cross':
        return position.initPositionMargin;
      case 'Isolated':
        return Num(isolatedMarginInitial).plus(isolatedMarginAdded).toString();
      default:
        return '0';
    }
  });
  const marginForPendingPositionAtom = atom((get) => {
    const positionQty = get(positionQtyAtom);
    const orderPrice = get(orderPriceAtom);
    const leverage = get(leverageAtom);
    return positionQty.multipliedBy(orderPrice).dividedBy(leverage).toString();
  });
  const marginAtom = positionType === 'open' ? marginForOpenPositionAtom : marginForPendingPositionAtom;

  const estimatedPnlAtom = atomWithBigNumber((get) => {
    const triggerPriceInputText = get(triggerPriceInputTextAtom);
    if (triggerPriceInputText === '') return Num(0);

    // Market TP/SL fills at approximately the trigger price. User-set Limit
    // TP/SL fills at the limit price — that's the P&L reference, not the
    // trigger. BBO has no pre-trigger price on the client (the matcher stamps
    // it at trigger time), so we fall back to the trigger price as the best
    // available estimate there.
    const useLimitPrice = get(isLimitTpslOrderAtom) && !get(isBboAtom);
    if (useLimitPrice && get(limitPriceInputIsEmptyAtom)) return Num(0);
    const exitPrice = useLimitPrice ? get(limitPriceInputValueAtom) : get(triggerPriceInputValueAtom);

    const entryOrOrderPrice = get(entryOrOrderPriceAtom);
    const qty = Num(get(tpslOrderQtyAtom) || '0');
    const side = get(sideAtom);
    const sideValue = side === 'Long' ? 1 : -1;

    return exitPrice.minus(entryOrOrderPrice).multipliedBy(qty).multipliedBy(sideValue);
  });
  const estimatedPnlPercentAtom = atomWithBigNumber((get) => {
    const estimatedPnl = get(estimatedPnlAtom);
    const margin = get(marginAtom);
    return estimatedPnl.dividedBy(margin).multipliedBy(100);
  });

  // 입력된 trigger price가 청산가에 근접(±2%)하면 실행 전 청산 경고를 띄운다.
  // native(iOS/Android) 및 TimeTrigger 모달과 동일한 기준. (청산가 근처 2% 이내)
  const isCloseToLiquidationAtom = atom((get) =>
    isTriggerCloseToLiquidation({
      side: get(sideAtom),
      triggerPrice: get(triggerPriceInputValueAtom),
      liqPrice: Num(get(liqPriceAtom)),
      isTriggerEmpty: get(triggerPriceInputIsEmptyAtom),
    }),
  );

  // 입력된 trigger price에 도달하면 전손인지
  const isTotalLossAtom = atom((get) => {
    if (marginType === 'Cross') return false;

    const side = get(sideAtom);
    const liqPrice = get(liqPriceAtom);
    const triggerPriceInputValue = get(triggerPriceInputValueAtom);
    const estimatedPnlPercent = get(estimatedPnlPercentAtom);
    if (estimatedPnlPercent.isLessThanOrEqualTo(-100)) return true;
    if (side === 'Long') return triggerPriceInputValue.isLessThanOrEqualTo(liqPrice);
    else return triggerPriceInputValue.isGreaterThanOrEqualTo(liqPrice);
  });

  return {
    priceDiffFromEntryPriceAtom,
    estimatedPnlAtom,
    estimatedPnlPercentAtom,
    isCloseToLiquidationAtom,
    isTotalLossAtom,
  };
});
tpslModalInputDerivationBunja.debugLabel = 'tpslModalInputDerivationBunja';
